import { useEffect } from "react";
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

let instance: Lenis | null = null;

/** 当前平滑滚动实例（手机/平板/减少动效时为 null，走原生滚动） */
export function getLenis() {
  return instance;
}

/** 回到页面顶部：有平滑滚动就让它立即定位，否则原生 */
export function scrollToTop() {
  if (typeof window === "undefined") return;
  const root = document.documentElement;
  // 临时关掉 CSS smooth，保证换页是瞬间归零而不是动画滚上去
  const prev = root.style.scrollBehavior;
  root.style.scrollBehavior = "auto";
  if (instance) instance.scrollTo(0, { immediate: true, force: true });
  window.scrollTo(0, 0);
  root.scrollTop = 0;
  document.body.scrollTop = 0;
  root.style.scrollBehavior = prev;
  // 新页面布局就绪后再重新测量固定区块
  requestAnimationFrame(() => ScrollTrigger.refresh());
}

if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  // 前进/后退不继承旧滚动位置，由我们统一瞬间归零
  window.history.scrollRestoration = "manual";
}

function eligible() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // 按输入设备判断而不是按宽度：编辑器预览窗口很窄但仍是鼠标/触控板；触屏设备一律原生滚动
  const fine = window.matchMedia("(any-hover: hover) and (any-pointer: fine)").matches;
  const touchOnly = window.matchMedia("(pointer: coarse)").matches && !fine;
  return !reduce && fine && !touchOnly;
}

/** 仅桌面端鼠标/触控板开启；与 GSAP 共用同一时钟，保证固定区块不抖 */
export function useSmoothScroll() {
  useEffect(() => {
    if (!eligible()) return undefined;

    const lenis = new Lenis({
      lerp: 0.09,
      wheelMultiplier: 1,
      smoothWheel: true,
      syncTouch: false,
    });
    instance = lenis;

    const onScroll = () => ScrollTrigger.update();
    lenis.on("scroll", onScroll);
    const tick = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    // 站内锚点（#contact、#main 等）走平滑滚动定位，保留导航栏留白
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.("a[href^=\"#\"]") as HTMLAnchorElement | null;
      if (!a) return;
      const id = a.getAttribute("href")?.slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { offset: -80 });
      if (id === "main") el.focus?.({ preventScroll: true });
    };
    document.addEventListener("click", onClick);

    requestAnimationFrame(() => ScrollTrigger.refresh());

    return () => {
      document.removeEventListener("click", onClick);
      gsap.ticker.remove(tick);
      gsap.ticker.lagSmoothing(500, 33);
      lenis.off("scroll", onScroll);
      lenis.destroy();
      instance = null;
    };
  }, []);
}